import { Modal } from 'components/core-ui/Modal'
import { Contract } from 'components/employee/interfaces'
import { useFormik } from 'formik'
import { useEffect, useMemo, useState } from 'react'
import { getInitialValues } from './helpers'
import { makeCorePatchRequest } from 'utils/api'
import { useMutation } from 'react-query'
import { useNotification } from 'hooks'
import { handleQueryError } from 'utils/helpers'
import { toEmployeeContractPayload, toEmployeeFormItemsList } from './serializers'
import { Button, IconButton } from '@fullscreendigital/carrefour-dex-fe'
import { getLocationString } from 'components/employee/helpers'
import { DeleteActionIcon, ShopIcon, ShopIconBlue } from 'icons'
import { Grid, List, ListItem, ListItemIcon, ListItemText, Typography } from '@mui/material'
import { FormInfoItems } from 'components/core-ui/FormInfoItems'
import { AsyncAutocomplete } from 'components/core-ui/AsyncAutocomplete'
import {
  getLocationSubtypesOptions,
  getLocationTypesOptions,
  getLocationUnitsOptions
} from 'components/employee/api'
import { CountyCitySelect } from 'components/core-ui/CountyCitySelect'
import { CountyCityInterface } from 'components/supplyomni/SelectClient/NewClient/interfaces'
import { getLocationUsageOptions } from 'components/employee/api'
import { ContractLocation } from 'components/employee/interfaces'
import s from 'components/core-ui/Modal/Modal.modules.scss'
import { FormItemList } from 'components/core-ui/FormItemList'
import ls from './EmployeeModal.modules.scss'

interface EmployeeModalProps {
  isModalOpen: boolean
  closeModal: Function
  setShouldRefreshData: Function
  editItem: Contract
}

const EmployeeModal = (props: EmployeeModalProps) => {
  const { isModalOpen, closeModal, editItem, setShouldRefreshData } = props

  const [contractLocationList, setContractLocationList] = useState<ContractLocation[]>([])

  useEffect(() => {
    if (editItem?.contractLocationList) {
      setContractLocationList(editItem.contractLocationList)
    }
  }, [editItem?.contractLocationList])

  const { showNotification } = useNotification()

  const initialValues = useMemo(() => getInitialValues(), [editItem])

  const mutateEmpoyeeContract = async values => {
    await makeCorePatchRequest(`/employee-management/contract/${editItem.id}`, values)
  }

  const { isLoading, mutate } = useMutation(mutateEmpoyeeContract, {
    onSuccess: res => {
      setShouldRefreshData(true)
      closeModal()
      formik.resetForm()
      showNotification({
        message: 'The employee contract was successfully updated.',
        type: 'success'
      })
    },
    onError: error =>
      handleQueryError(error, 'Error for updating employee contract.', showNotification)
  })

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: initialValues,
    onSubmit: async values => {
      mutate(toEmployeeContractPayload(contractLocationList))
    }
  })

  const cancelModal = () => {
    closeModal()
    formik.resetForm()
  }

  const { data: locationTypesOptions } = getLocationTypesOptions(showNotification)
  const { data: locationSubtypesOptions } = getLocationSubtypesOptions(
    showNotification,
    formik.values.locationType?.value
  )
  const { data: locationUsageOptions } = getLocationUsageOptions(
    showNotification,
    formik.values.locationType?.value,
    formik.values.locationSubtype?.value,
    +formik.values.county?.id,
    +formik.values.city?.id
  )
  const { data: locationUnitsOptions } = getLocationUnitsOptions(
    showNotification,
    formik.values.locationUsage?.value
  )

  const addContractLocation = () => {
    const newContractLocationList = [
      ...contractLocationList,
      {
        id: Date.now(),
        locationUnit: {
          id: +formik.values.locationUnit.value,
          name: formik.values.locationUnit.label,
          locationUsage: {
            id: +formik.values.locationUsage.value,
            name: formik.values.locationUsage.label
          }
        }
      }
    ]
    setContractLocationList(newContractLocationList)
    formik.resetForm()
  }

  const removeContractLocation = (itemId: number) => {
    const newContractLocationList = contractLocationList.filter(
      contractLocation => contractLocation.id !== itemId
    )

    setContractLocationList(newContractLocationList)
  }

  return (
    <Modal
      isModalOpen={isModalOpen}
      closeModal={cancelModal}
      modalTitle="Edit employee contract"
      modalWidth="50%"
    >
      <FormInfoItems
        items={[
          { label: 'Job title:', name: editItem?.jobTitle },
          { label: 'Employee type: ', name: editItem?.employeeType },
          { label: 'Status: ', name: editItem?.status }
        ]}
      />
      <FormItemList
        items={toEmployeeFormItemsList(contractLocationList)}
        startIcon={<ShopIcon />}
        deleteHandler={removeContractLocation}
        title="Contract locations"
      />
      <Typography variant="h6" className={ls.titleContainer}>
        <span className={ls.title}>Add a new location for contract</span>
      </Typography>
      <Grid container spacing={4}>
        <Grid xs={12} md={6} item>
          <AsyncAutocomplete
            variant="outlined"
            name="locationType"
            label="Type"
            value={formik.values.locationType}
            options={locationTypesOptions || []}
            onChange={value => {
              formik.setFieldValue('locationSubtype', null)
              formik.setFieldValue('locationUsage', null)
              formik.setFieldValue('locationType', value)
            }}
            error={!!formik.errors.locationType}
            helperText={formik.errors.locationType as string}
            required
          />
        </Grid>
        <Grid xs={12} md={6} item>
          <AsyncAutocomplete
            variant="outlined"
            name="locationSubtype"
            label="Subtype"
            value={formik.values.locationSubtype}
            options={locationSubtypesOptions || []}
            onChange={value => {
              formik.setFieldValue('locationUsage', null)
              formik.setFieldValue('locationSubtype', value)
            }}
            error={!!formik.errors.locationSubtype}
            helperText={formik.errors.locationSubtype as string}
            required
          />
        </Grid>
        <Grid item xs={12}>
          <CountyCitySelect
            required
            county={{
              action: {
                url: '/employee-management/counties',
                type: 'GET',
                valueKey: 'id',
                labelKey: 'name'
              },
              width: 100,
              label: 'County',
              variant: 'outlined'
            }}
            city={{
              action: {
                url: '/employee-management/cities',
                type: 'GET',
                valueKey: 'id',
                labelKey: 'name'
              },
              width: 100,
              label: 'City',
              variant: 'outlined'
            }}
            value={{
              county: { value: formik.values.county.id, label: formik.values.county.name },
              city: { value: formik.values.city.id, label: formik.values.city.name }
            }}
            onChange={(newValue: CountyCityInterface, fieldToUpdate: string) => {
              formik.setFieldValue(fieldToUpdate, {
                id: newValue?.value,
                name: newValue?.label
              })
            }}
            error={!!formik.errors.county || !!formik.errors.city}
            helperText="required"
          />
        </Grid>
        <Grid xs={12} md={6} item>
          <AsyncAutocomplete
            variant="outlined"
            name="locationUsage"
            label="Location usage"
            value={formik.values.locationUsage}
            options={locationUsageOptions || []}
            onChange={value => {
              formik.setFieldValue('locationUsage', value)
            }}
            error={!!formik.errors.locationUsage}
            helperText={formik.errors.locationUsage as string}
            required
          />
        </Grid>
        <Grid xs={12} md={6} item>
          <AsyncAutocomplete
            variant="outlined"
            name="locationUnit"
            label="Location unit"
            value={formik.values.locationUnit}
            options={locationUnitsOptions || []}
            onChange={value => formik.setFieldValue('locationUnit', value)}
            error={!!formik.errors.locationUnit}
            helperText={formik.errors.locationUnit as string}
            required
            groupBy={option => option.groupBy}
          />
        </Grid>
        <Grid xs={12} md={6} item>
          <IconButton
            startIcon={<ShopIconBlue />}
            variant="outlined"
            label="Add location for contract"
            disabled={!formik.values?.locationUnit}
            onClick={addContractLocation}
          />
        </Grid>
        <Grid xs={12} md={6} item></Grid>
        <Grid xs={12} item className={s.modalButtons}>
          <Button variant="outlined" label="Cancel" onClick={cancelModal} />
          <Button
            variant="contained"
            type="submit"
            label="Submit"
            onClick={() => formik.handleSubmit()}
            disabled={isLoading}
          />
        </Grid>
      </Grid>
    </Modal>
  )
}

export default EmployeeModal
